import { Suspense, useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { WebGPURenderer } from "three/webgpu";
import { Canvas } from "@react-three/fiber";
import { Physics } from "@react-three/cannon";
import GameControls, { MoveDir, LookDelta } from "./GameControls";
import Floor from "./Floor";
import Wall from "./Wall";
import Player from "./Player";
import Mezzanine from "./hallway_2/Mezzanine";
import Room from "./room1/room/Room";
import Room2 from "./room2/room/Room2";
import Elevator from "./elevator/Elevator";
import WallDecorations from "./hallway/WallDecorations";
import Bookshelf from "./hallway/Bookshelf";
import Bee from "./hallway/Bee";
import AcCleaning from "./room1/ac_cleaning/AcCleaning";
import ChildCare from "./room1/child_care/ChildCare";
import CleaningMonthly from "./room1/cleaning_monthly/CleaningMonthly";
import DeepCleaning from "./room1/deep_cleaning/DeepCleaning";
import DisinfectionService from "./room1/disinfection_service/DisinfectionService";
import ElderlyCare from "./room1/elderly_care/ElderlyCare";
import GroceryAssistant from "./room1/grocery_assistant/GroceryAssistant";
import HomeCooking from "./room1/home_cooking/HomeCooking";
import BBeauty from "./room2/bbeauty/BBeauty";
import HomeMoving from "./room2/home_moving/HomeMoving";
import HouseKeeping from "./room2/house_keeping/HouseKeeping";
import IndustrialCleaning from "./room2/industrial_cleaning/IndustrialCleaning";
import IroningService from "./room2/ironing_service/IroningService";
import Laundry from "./room2/laundry/Laundry";
import PatientCare from "./room2/patient_care/PatientCare";
import UpholsteryService from "./room2/upholstery_service/UpholsteryService";
import { WALL_OFFSET_X, WALL_OFFSET_Z, WALL_POSITION_Y, ROOF_HEIGHT, FLOOR_DIMENSIONS } from "../lib/consts";
import { isMobile } from "../lib/functions";
import { TextureProvider } from "../lib/context/TextureContext";
import GlobalLights from "./GlobalLights";
import { PRESET } from "../lib/lighting-presets";
import { PlayerProvider } from "../lib/context/PlayerContext";


function BuildingCeiling() {
	const texRef = useRef<THREE.CanvasTexture | null>(null);
	const meshRef = useRef<THREE.Mesh>(null);

	const tex = useMemo(() => {
		const spacing = 3;
		const cellsW = Math.ceil(30 / spacing);
		const cellsZ = Math.ceil(40 / spacing);
		const scale = 70;
		const cw = cellsW * scale;
		const ch = cellsZ * scale;

		const canvas = document.createElement("canvas");
		canvas.width = cw;
		canvas.height = ch;
		const ctx = canvas.getContext("2d")!;
		ctx.fillStyle = "#6b4b32";
		ctx.fillRect(0, 0, cw, ch);

		ctx.strokeStyle = "#ffffff";
		ctx.lineWidth = 2;
		for (let i = 0; i <= cellsW; i++) {
			ctx.beginPath();
			ctx.moveTo(i * scale, 0);
			ctx.lineTo(i * scale, ch);
			ctx.stroke();
		}
		for (let i = 0; i <= cellsZ; i++) {
			ctx.beginPath();
			ctx.moveTo(0, i * scale);
			ctx.lineTo(cw, i * scale);
			ctx.stroke();
		}

		const texture = new THREE.CanvasTexture(canvas);
		texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
		texture.needsUpdate = true;
		
		texRef.current = texture;
		return texture;
	}, []);

	useEffect(() => {
		return () => {
			// Dispose texture and material when component unmounts
			if (meshRef.current) {
				if (meshRef.current.material instanceof THREE.Material) {
					meshRef.current.material.dispose();
				}
			}
			if (texRef.current) {
				texRef.current.dispose();
			}
		};
	}, []);

	const emissiveIntensity = PRESET.ceilingEmissive;

	return (
		<mesh
			ref={meshRef}
			position={[0, ROOF_HEIGHT - 0.05, 0]}
			rotation={[-Math.PI / 2, 0, 0]}
			receiveShadow
		>
			<planeGeometry args={FLOOR_DIMENSIONS} />
			{tex && (
				<meshStandardMaterial
					color="#bf8045"
					emissiveMap={tex}
					emissive="#ffffff"
					emissiveIntensity={emissiveIntensity}
					roughness={0.7}
					side={THREE.DoubleSide}
				/>
			)}
		</mesh>
	);
}


function ShopContent() {
	const canvasRef = useRef<WebGPURenderer>(null);
	// These refs are updated by GameControls UI and read every frame inside Player.
	// Using refs (not state) avoids re-rendering the Canvas tree on every input sample.
	const moveRef = useRef<MoveDir>({ forward: false, backward: false, left: false, right: false });
	const lookDeltaRef = useRef<LookDelta>({ dx: 0, dy: 0 });
	const jumpRef = useRef(false);

	useEffect(() => {
		document.title = "3D Virtual Shop";
		const renderer = canvasRef.current;
		return () => {
			renderer?.dispose();
		};
	}, []);

	useEffect(() => {
		const renderer = canvasRef.current;
		if (renderer) {
			renderer.toneMappingExposure = PRESET.exposure;
		}
	}, []);

	const handleCanvasClick = () => {
		// no-op: pointer lock no longer used
	};

	return (
		<div
			style={{ width: "100vw", height: "100vh" }}
			onClick={handleCanvasClick}
		>
			{/* Game controls overlay — shown on both desktop and mobile */}
			<GameControls
				onMoveChange={(m) => { moveRef.current = m; }}
				onLookDelta={(d) => {
					lookDeltaRef.current.dx += d.dx;
					lookDeltaRef.current.dy += d.dy;
				}}
				onJump={() => { jumpRef.current = true; }}
			/>

			<Canvas
				style={{ width: "100vw", height: "100vh" }}
				// Mobile GPUs are fill-rate bound; rendering at full retina DPR (2x)
				// quadruples fragment work and is the main cause of thermal throttling.
				dpr={isMobile() ? [1, 1.5] : [1, 2]}
				shadows
				gl={async ({ canvas }) => {
					// Decide the backend BEFORE constructing the renderer. A <canvas>
					// permanently keeps the first context type it is given, so building
					// a second WebGPURenderer on the same canvas after a failed WebGPU
					// init always throws. Previously that second init ran outside any
					// try/catch, so the rejection bubbled up, the <Canvas> never mounted,
					// and WebGPU-less devices froze forever on the loading screen.
					const supportsWebGPU = async () => {
						// navigator.gpu isn't in this project's DOM lib typings.
						const gpu = (navigator as unknown as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
						if (typeof navigator === "undefined" || !gpu) return false;
						try {
							// Some mobile drivers never resolve requestAdapter(); race it
							// against a timeout so a stuck probe can't hang the whole app.
							const adapter = await Promise.race([
								gpu.requestAdapter(),
								new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
							]);
							return !!adapter;
						} catch {
							return false;
						}
					};

					const forceWebGL = !(await supportsWebGPU());
					const renderer = new WebGPURenderer({ canvas, antialias: true, forceWebGL });
					try {
						await renderer.init();
					} catch (err) {
						// Last-resort: log loudly instead of hanging silently. Nothing can
						// recover on this canvas at this point (its context is consumed).
						console.error("[virtual-eshop] Renderer init failed (WebGPU:", !forceWebGL, "):", err);
						throw err;
					}
					renderer.shadowMap.enabled = true;
					renderer.shadowMap.type = THREE.PCFSoftShadowMap;
					renderer.toneMapping = THREE.ACESFilmicToneMapping;
					renderer.toneMappingExposure = PRESET.exposure;
					return renderer;
				}}
				camera={{ fov: 55, near: 0.1, far: 100 }}
				onCreated={({ gl, camera }) => {
					canvasRef.current = gl;
					camera.updateProjectionMatrix();
				}}
			>
				<TextureProvider>
					<Suspense fallback={null}>
						<GlobalLights />
						<Physics gravity={[0, -9.81, 0]} iterations={10} broadphase="SAP">
							<Player moveRef={moveRef} lookDeltaRef={lookDeltaRef} jumpRef={jumpRef} />
							<group>
								<Floor />
								<Mezzanine />
								<BuildingCeiling />
								<Wall position={[0, WALL_POSITION_Y, -WALL_OFFSET_Z]} />
								<Wall position={[0, WALL_POSITION_Y, WALL_OFFSET_Z]} />
								<Wall
									position={[-WALL_OFFSET_X, WALL_POSITION_Y, 0]}
									rotation={[0, Math.PI / 2, 0]}
								/>
								<Wall
									position={[WALL_OFFSET_X, WALL_POSITION_Y, 0]}
									rotation={[0, Math.PI / 2, 0]}
								/>
							</group>
							<Room />
							<Room2 />
							{/* Single elevator component handles both floor 1 and floor 2 */}
							<Elevator position={[3, 0, -13]} />
							<AcCleaning />
							<ChildCare />
							<CleaningMonthly />
							<DeepCleaning />
							<DisinfectionService />
							<ElderlyCare />
							<GroceryAssistant />
							<HomeCooking />
							<BBeauty />
							<HomeMoving />
							<HouseKeeping />
							<IndustrialCleaning />
							<IroningService />
							<Laundry />
							<PatientCare />
							<UpholsteryService />
							<WallDecorations />
							<Bookshelf position={[-12.5, 0, 17]} rotation={[0, Math.PI / 2, 0]} />
							<Bee position={[1, 1.6, 13]} rotation={[0, -Math.PI / 2, 0]} />
						</Physics>
					</Suspense>
				</TextureProvider>
			</Canvas>
		</div>
	);
}

export default function VirtualShop() {
	return (
		<PlayerProvider>
			<ShopContent />
		</PlayerProvider>
	);
}
